import {
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_PURCHASE_ORDER
} from '@klear/constants'
import type { Invoice } from '@klear/invoice/invoice'
import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import type { Operational } from '@klear/operational'
import type { ProofOfAcceptance } from '@klear/proof-of-acceptance'
import type { PurchaseOrder } from '@klear/purchase-order'
import type { OrderEventDomainItem } from '@klear/order-event/order-event-domain'
import type { OrderEventStatusViewFor } from '@klear/order-event/status-view'

export type OrderEventListEnvelope<TType extends OrderEventDomainListType = OrderEventDomainListType> =
  OrderEventStatusViewFor<TType> & {
    idDisplay: string
    orderIdDisplay: string
    updatedAt: string
    traderId?: number
    traderName?: string
    customerId?: number
    customerName?: string
    parentId?: number
    parentIdDisplay?: string
    createdBy?: { firstName: string | null; lastName: string | null } | null
  }

export type OrderEventDomainListItem =
  | {
      type: typeof PERMISSION_ENTITY_PURCHASE_ORDER
      model: PurchaseOrder
      envelope: OrderEventListEnvelope<typeof PERMISSION_ENTITY_PURCHASE_ORDER>
    }
  | {
      type: typeof PERMISSION_ENTITY_INVOICE
      model: Invoice
      envelope: OrderEventListEnvelope<typeof PERMISSION_ENTITY_INVOICE>
    }
  | {
      type: typeof PERMISSION_ENTITY_OPERATIONAL
      model: Operational
      envelope: OrderEventListEnvelope<typeof PERMISSION_ENTITY_OPERATIONAL>
    }
  | {
      type: typeof PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE
      model: ProofOfAcceptance
      envelope: OrderEventListEnvelope<typeof PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE>
    }
  | {
      type: typeof PERMISSION_ENTITY_INVOICE_OFFER
      model: InvoiceOffer
      envelope: OrderEventListEnvelope<typeof PERMISSION_ENTITY_INVOICE_OFFER>
    }

export const ORDER_EVENT_DOMAIN_LIST_TYPES = [
  PERMISSION_ENTITY_PURCHASE_ORDER,
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_INVOICE_OFFER
] as const

export type OrderEventDomainListType = (typeof ORDER_EVENT_DOMAIN_LIST_TYPES)[number]

export function isOrderEventDomainListType(type: string | null | undefined): type is OrderEventDomainListType {
  return type != null && (ORDER_EVENT_DOMAIN_LIST_TYPES as readonly string[]).includes(type)
}

export type OrderEventListEnvelopeFields = {
  idDisplay: string | null | undefined
  orderIdDisplay: string | null | undefined
  updatedAt: string
  parentId?: number | null
  parentIdDisplay?: string | null
  traderId?: number | null
  traderName?: string | null
  customerId?: number | null
  customerName?: string | null
  createdBy?: OrderEventListEnvelope['createdBy']
}

export function orderEventListEnvelope<TType extends OrderEventDomainListType>(
  type: TType,
  statusView: OrderEventStatusViewFor<NoInfer<TType>>,
  fields: OrderEventListEnvelopeFields
): OrderEventListEnvelope<TType> {
  void type
  const { lifecycleState, status, statusLabel } = statusView
  const { idDisplay, orderIdDisplay, updatedAt, createdBy } = fields
  const envelope = {
    idDisplay: idDisplay ?? '',
    orderIdDisplay: orderIdDisplay ?? '',
    lifecycleState,
    status,
    statusLabel,
    updatedAt,
    traderId: fields.traderId != null && fields.traderId > 0 ? fields.traderId : undefined,
    traderName: fields.traderName ?? undefined,
    customerId: fields.customerId != null && fields.customerId > 0 ? fields.customerId : undefined,
    customerName: fields.customerName ?? undefined,
    parentId: fields.parentId != null && fields.parentId > 0 ? fields.parentId : undefined,
    parentIdDisplay:
      fields.parentIdDisplay != null && fields.parentIdDisplay !== '' ? fields.parentIdDisplay : undefined,
    createdBy: createdBy
  }
  return envelope as unknown as OrderEventListEnvelope<TType>
}

export function withListEnvelope<TType extends OrderEventDomainItem['type']>(
  item: Extract<OrderEventDomainItem, { type: TType }>,
  envelope: OrderEventListEnvelope<NoInfer<TType>>
): Extract<OrderEventDomainListItem, { type: TType }> {
  return { ...item, envelope } as Extract<OrderEventDomainListItem, { type: TType }>
}

export function domainListItemId(item: OrderEventDomainListItem): number {
  return item.model.id ?? 0
}

export function domainListItemParentId(item: OrderEventDomainListItem): number | undefined {
  switch (item.type) {
    case PERMISSION_ENTITY_INVOICE:
      return item.model.parentId ?? item.envelope.parentId
    case PERMISSION_ENTITY_INVOICE_OFFER:
      return item.model.parentId > 0 ? item.model.parentId : item.envelope.parentId
    case PERMISSION_ENTITY_OPERATIONAL:
    case PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE:
      return item.model.parentId ?? item.envelope.parentId
    case PERMISSION_ENTITY_PURCHASE_ORDER:
      return item.envelope.parentId
  }
}
